// 가입 1단계 폼 — 이름·업무용 이메일(비밀번호는 메일 링크를 누른 뒤 정한다) · 성공하면 onSent 로 보낸 화면으로(이메일은 주소창에 싣지 않는다).

import { FormEvent, ReactNode, RefObject, useEffect, useRef, useState, useTransition } from "react";
import { toast } from "sonner";

import { LOGIN_PATH, nameInputMax, PERSON_NAME_MAX, type SignupField } from "@buildpy/core/modules/onboarding/rules";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { selfSignup } from "@/api/actions/onboarding";
import { Link } from "@/lib/nav";
import { turnstileSiteKeyOf, type TurnstileFailure } from "@/lib/turnstile";
import {
  createErrorAnnouncer,
  focusSignupError,
  precheckSignup,
  SIGNUP_FAILED_MESSAGE,
  type SignupFormError,
  signupErrorOf,
  turnstileFailureError,
  withTurnstile,
} from "./signup-form-state";
import TurnstileWidget from "./turnstile-widget";

const errorIdOf = (field: SignupField): string => `signup-${field}-error`;

interface ViewProps {
  pending: boolean;
  error: SignupFormError | null;
  formRef?: RefObject<HTMLFormElement | null>;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  onFieldChange?: (field: SignupField) => void;
  // 사이트 키가 있을 때만 들어오는 Turnstile 자리.
  turnstile?: ReactNode;
  // Turnstile 을 못 불러왔거나 확인이 실패했을 때 오류 줄의 「다시 시도」.
  onRetryTurnstile?: () => void;
}

export const SignupFormView = ({ pending, error, formRef, onSubmit, onFieldChange, turnstile, onRetryTurnstile }: ViewProps) => {
  const fieldError = (field: SignupField) => (error?.field === field ? error.message : null);
  const nameError = fieldError("name");
  const emailError = fieldError("email");
  return (
    <form className="flex flex-col gap-y-4" data-testid="signup-form" onSubmit={onSubmit} ref={formRef}>
      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="signup-name">이름</Label>
        <Input
          aria-describedby={nameError ? `${errorIdOf("name")} signup-name-hint` : "signup-name-hint"}
          aria-invalid={nameError ? true : undefined}
          autoComplete="name"
          disabled={pending}
          id="signup-name"
          maxLength={nameInputMax(PERSON_NAME_MAX)}
          name="name"
          onChange={() => onFieldChange?.("name")}
          required
        />
        {/* live 영역은 늘 있고 글만 바뀐다(이미 포커스된 칸에서도 읽힌다) · 비면 empty:-mt-1.5 로 칸 간격을 되돌린다 */}
        <p aria-live="polite" className="text-xs text-destructive empty:-mt-1.5" data-testid={errorIdOf("name")} id={errorIdOf("name")}>
          {nameError}
        </p>
        <p className="text-xs text-muted-foreground" id="signup-name-hint">
          멤버 목록과 고객 알림에 보이는 이름입니다
        </p>
      </div>

      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="signup-email">업무용 이메일</Label>
        <Input
          aria-describedby={emailError ? errorIdOf("email") : undefined}
          aria-invalid={emailError ? true : undefined}
          autoComplete="email"
          disabled={pending}
          id="signup-email"
          inputMode="email"
          maxLength={254}
          name="email"
          onChange={() => onFieldChange?.("email")}
          placeholder="you@company.com"
          required
          type="email"
        />
        <p aria-live="polite" className="text-xs text-destructive empty:-mt-1.5" data-testid={errorIdOf("email")} id={errorIdOf("email")}>
          {emailError}
        </p>
      </div>

      {turnstile}

      {error && !error.field && (
        <p className="text-sm text-destructive" data-testid="signup-error" role="alert">
          {error.message}
          {error.retry === "turnstile" && onRetryTurnstile && (
            <>
              {" "}
              <Button data-testid="signup-turnstile-retry" onClick={onRetryTurnstile} size="inline" type="button" variant="link-destructive">
                다시 시도
              </Button>
            </>
          )}
        </p>
      )}

      <Button className="w-full" disabled={pending} type="submit">
        {pending ? "보내는 중…" : "가입하고 확인 메일 받기"}
      </Button>

      <p className="text-sm text-muted-foreground">
        이미 계정이 있나요?{" "}
        <Link className="font-medium text-primary underline underline-offset-4 hover:text-foreground" href={LOGIN_PATH}>
          로그인
        </Link>
      </p>
    </form>
  );
};

interface Props {
  onSent: (sent: { email: string }) => void;
  // 서버가 「가입 준비 중」(메일 dry-run)이라 답하면 폼 대신 안내를 띄운다.
  onUnavailable?: () => void;
  // 빌드 때 박힌 사이트 키 — 테스트만 바꿔 넣는다.
  turnstileSiteKey?: string | null;
}

const SignupForm = ({ onSent, onUnavailable, turnstileSiteKey = turnstileSiteKeyOf(import.meta.env) }: Props) => {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<SignupFormError | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [turnstileFailed, setTurnstileFailed] = useState<TurnstileFailure | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [announcer] = useState(() => createErrorAnnouncer(setError));

  // 떠날 때 예약된 재안내를 취소한다.
  useEffect(() => announcer.dispose, [announcer]);

  // 위젯을 못 불러오거나 확인이 실패하면 바로 폼 아래 안내를 띄운다 · 위젯이 스스로 풀리면 그 안내만 거둔다.
  useEffect(() => {
    if (turnstileFailed) announcer.announce(turnstileFailureError(turnstileFailed));
    else announcer.turnstileCleared();
  }, [turnstileFailed, announcer]);

  // 칸이 풀린 뒤(보내는 중엔 disabled 라 포커스가 안 간다) 오류 칸으로 옮긴다.
  useEffect(() => {
    if (!pending) focusSignupError(formRef.current, error);
  }, [error, pending]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    const input = { name: String(form.get("name") ?? ""), email };
    const precheck = precheckSignup(input);
    const guarded = withTurnstile(input, turnstileSiteKey, turnstileToken, turnstileFailed);
    // 다시 보낼 때 옛 오류를 지운다(옛 칸으로 포커스가 튀지 않게) · 사전 검사 오류는 다음 프레임에 다시 넣어 같은 오류도 다시 읽힌다.
    announcer.announce(precheck ?? (guarded.ok ? null : guarded.error));
    if (precheck || !guarded.ok) return;
    // 토큰은 서버 검증 한 번으로 끝난다 — 실패해 다시 보낼 땐 새 토큰을 받는다.
    const retryTurnstile = () => {
      if (!turnstileSiteKey) return;
      setTurnstileToken(null);
      setTurnstileReset((n) => n + 1);
    };

    startTransition(async () => {
      try {
        const result = await selfSignup(guarded.input);
        if (!result.ok) {
          if (result.unavailable && onUnavailable) {
            onUnavailable();
            return;
          }
          retryTurnstile();
          setError(signupErrorOf(result));
          return;
        }
        setError(null);
        onSent({ email: email.toLowerCase() });
      } catch {
        retryTurnstile();
        setError({ message: SIGNUP_FAILED_MESSAGE, field: null });
        toast.error(SIGNUP_FAILED_MESSAGE);
      }
    });
  };

  const onFieldChange = announcer.fieldChanged;

  // 다시 그려 스크립트를 다시 받는다(로더는 실패하면 태그를 떼 둔다) · 그 안내만 지운다.
  const onRetryTurnstile = () => {
    setTurnstileFailed(null);
    setTurnstileToken(null);
    setTurnstileReset((n) => n + 1);
    announcer.dispose();
    setError((current) => (current?.retry === "turnstile" ? null : current));
  };

  const turnstile = turnstileSiteKey ? (
    <TurnstileWidget onFailedChange={setTurnstileFailed} onToken={setTurnstileToken} resetKey={turnstileReset} siteKey={turnstileSiteKey} />
  ) : null;

  return (
    <SignupFormView
      error={error}
      formRef={formRef}
      onFieldChange={onFieldChange}
      onRetryTurnstile={onRetryTurnstile}
      onSubmit={onSubmit}
      pending={pending}
      turnstile={turnstile}
    />
  );
};

export default SignupForm;
